import { SyntaxIcon } from '@expressable/syntax-ds';
import { Menu } from '@headlessui/react';
import { ArrowRight, Plus, SignOut } from '@phosphor-icons/react';
import cx from 'classnames';
import { Icon } from 'components/Icon';
import { Link } from 'components/Link';
import { FullClient, fullName } from 'domains/client';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useTranslation } from 'react-i18next';
import { routes } from 'router/routes';
import { useTrack } from 'utils/hooks/useMixPanel';

const linkClassName = (active: boolean) =>
  cx('text-left w-full pl-24 pr-16 py-[15px] text-sm leading-tight flex items-center gap-e8 justify-between', {
    'bg-gray-100 text-gray-900': active,
    'bg-white text-gray-700': !active,
  });

type Props = {
  currentClient: FullClient;
  otherClients: FullClient[];
  onLogOut: () => void;
  onSelectClient: (clientID: string, shouldRedirectToReferrer: boolean) => void;
};

export const MenuLinks = ({ currentClient, otherClients, onLogOut, onSelectClient }: Props): JSX.Element => {
  const { t } = useTranslation();

  const { track } = useTrack();
  return (
    <div className="divide-y divide-gray-100">
      <div>
        <div className="flex items-center justify-between w-full pl-24 pr-16 py-[15px] text-sm bg-white text-gray-900">
          <div data-testid="menu-links-current-client-name">
            <p className="font-semibold">{fullName(currentClient)}</p>
            <span className="text-xs text-gray-600 leading-none">{t('accountMenu.currentProfileHeading')}</span>
          </div>

          <Icon name="Check" className="text-primary-500 ml-12" />
        </div>

        {otherClients.map(client => (
          <Menu.Item key={client.clientID}>
            {({ active }) => (
              <button
                type="button"
                onClick={() => onSelectClient(client.clientID, false)}
                className={linkClassName(active)}
              >
                {fullName(client)}
              </button>
            )}
          </Menu.Item>
        ))}
      </div>
      <div>
        <Menu.Item>
          {({ active }) => (
            <Link
              to={routes.signupDirect.base}
              className={cx(linkClassName(active), 'flex items-center justify-between')}
              disableExternalLinkIcon
              data-testid="accountMenu-newClient"
              onClick={() => track(DIRECT_SIGNUP_MIXPANEL_EVENTS.PORTAL_NAVBAR.SIGN_UP_NEW_CLIENT)}
            >
              {t('accountMenu.signUpNewClientButtonText')}
              <SyntaxIcon icon={Plus} />
            </Link>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <div data-testid="menu-links-accountMenu-contactSupportButtonText">
              <Link to={routes.openSupportCase} className={linkClassName(active)} disableExternalLinkIcon>
                {t('accountMenu.contactSupportButtonText')}

                <SyntaxIcon icon={ArrowRight} />
              </Link>
            </div>
          )}
        </Menu.Item>
      </div>
      <div data-testid="menu-links-accountMenu-signOutButtonText">
        <Menu.Item>
          {({ active }) => (
            <button type="button" onClick={onLogOut} className={cx(linkClassName(active))}>
              {t('accountMenu.signOutButtonText')}
              <SyntaxIcon icon={SignOut} />
            </button>
          )}
        </Menu.Item>
      </div>
    </div>
  );
};
